/**
 * Localización — §16.1.
 *
 * La configuración actual de MaintainX (USD, MM/DD/AAAA, 12 h) es una fuente de
 * errores de captura para una operación peruana. Aquí queda fijada la corregida:
 * PEN, DD/MM/AAAA, 24 h, America/Lima.
 */
export const ZONA_HORARIA = 'America/Lima';
export const LOCALE = 'es-PE';
export const MONEDA_BASE = 'PEN';
/** IGV peruano, usado solo para mostrar el costo de licencia con impuestos. */
export const TASA_IGV = 0.18;

const fmtMoneda = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: MONEDA_BASE,
  minimumFractionDigits: 2,
});

const fmtFecha = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const fmtFechaHora = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Importe en soles: S/ 1,234.50 */
export function formatearMoneda(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  const n = typeof valor === 'string' ? Number(valor) : valor;
  return Number.isFinite(n) ? fmtMoneda.format(n) : '—';
}

/**
 * Fecha en DD/MM/AAAA, siempre en hora de Lima.
 *
 * Una fecha sola (`AAAA-MM-DD`, como las columnas `date`) se reordena tal cual: pasarla por
 * `new Date` la toma como medianoche UTC y en Lima caería en el día anterior.
 */
export function formatearFecha(valor: Date | string | null | undefined): string {
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [anio, mes, dia] = valor.split('-');
    return `${dia}/${mes}/${anio}`;
  }
  const d = aFecha(valor);
  return d ? fmtFecha.format(d) : '—';
}

/** Fecha y hora en DD/MM/AAAA HH:MM (24 h), siempre en hora de Lima. */
export function formatearFechaHora(valor: Date | string | null | undefined): string {
  const d = aFecha(valor);
  return d ? fmtFechaHora.format(d).replace(', ', ' ') : '—';
}

function aFecha(valor: Date | string | null | undefined): Date | null {
  if (!valor) return null;
  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Antigüedad en días — base del control de la bandeja de solicitudes (§9.4). */
export function diasTranscurridos(desde: Date | string, hasta: Date | string = new Date()): number {
  const a = aFecha(desde);
  const b = aFecha(hasta);
  if (!a || !b) return 0;
  return Math.floor((b.getTime() - a.getTime()) / 86_400_000);
}

/** «2 h 30 min» a partir de minutos — el tiempo estimado se captura así (§10.3). */
export function formatearDuracion(minutos: number | null | undefined): string {
  if (minutos === null || minutos === undefined || minutos < 0) return '—';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/**
 * Normaliza un texto para comparaciones de unicidad: sin acentos, sin
 * mayúsculas, sin espacios redundantes.
 *
 * Es lo que impide que «AC - No enciende» y «AC- No Enciende» vuelvan a convivir
 * como dos tipos de falla distintos (§7.4).
 */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Distancia de Levenshtein, para la detección de similitud al crear un tipo de falla (§7.4). */
export function distanciaTexto(a: string, b: string): number {
  const s = normalizar(a);
  const t = normalizar(b);
  if (s === t) return 0;
  const filas = s.length + 1;
  const cols = t.length + 1;
  let anterior = Array.from({ length: cols }, (_, j) => j);
  for (let i = 1; i < filas; i++) {
    const actual = [i, ...new Array<number>(cols - 1).fill(0)];
    for (let j = 1; j < cols; j++) {
      const costo = s[i - 1] === t[j - 1] ? 0 : 1;
      actual[j] = Math.min(actual[j - 1]! + 1, anterior[j]! + 1, anterior[j - 1]! + costo);
    }
    anterior = actual;
  }
  return anterior[cols - 1]!;
}

/** Dos nombres son «parecidos» si difieren en menos del 20 % de su longitud. */
export function esSimilar(a: string, b: string): boolean {
  const largo = Math.max(normalizar(a).length, normalizar(b).length);
  if (largo === 0) return false;
  return distanciaTexto(a, b) / largo < 0.2;
}
